import { useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, newKey } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInvalidateLedger, useJournal, useLedgerAccounts, useReconciliation, useTrialBalance } from '../api/queries';
import { addDays, formatDate, formatGhs, parseCedis } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

type Tab = 'tb' | 'journal' | 'chart';
const TYPE_LABEL: Record<string, string> = { ASSET: 'Asset', LIABILITY: 'Liability', EQUITY: 'Equity', REVENUE: 'Revenue', EXPENSE: 'Expense' };

export function Accounts() {
  const { me } = useAuth();
  const rec = useReconciliation();
  const [tab, setTab] = useState<Tab>('tb');
  const today = me?.property.today ?? '';

  return (
    <div className="page">
      <PageHead eyebrow="Accounts" title="The books" />
      {rec.data ? (
        rec.data.balanced ? (
          <div className="banner good">Books and folios agree: guests owe {formatGhs(rec.data.guestLedger)} on both sides.</div>
        ) : (
          <div className="banner bad" role="alert">
            Books and folios do not agree: the ledger says {formatGhs(rec.data.guestLedger)} but folios say {formatGhs(rec.data.folios)} (difference {formatGhs(rec.data.difference)}). Do not close the day until this is explained.
          </div>
        )
      ) : null}
      <div className="seg" role="group" aria-label="Accounts area">
        <button type="button" aria-pressed={tab === 'tb'} onClick={() => setTab('tb')}>
          Trial balance
        </button>
        <button type="button" aria-pressed={tab === 'journal'} onClick={() => setTab('journal')}>
          Journal
        </button>
        <button type="button" aria-pressed={tab === 'chart'} onClick={() => setTab('chart')}>
          Chart of accounts
        </button>
      </div>
      {tab === 'tb' ? <TrialBalanceView today={today} /> : tab === 'journal' ? <JournalView today={today} /> : <ChartView />}
    </div>
  );
}

/* ------------------------------------------------------------ trial balance */
function TrialBalanceView({ today }: { today: string }) {
  const [from, setFrom] = useState(addDays(today, -30));
  const [to, setTo] = useState(today);
  const tb = useTrialBalance(from, to);

  return (
    <div className="stack">
      <div className="grid-2">
        <Field id="tb-from" label="From">
          <input id="tb-from" className="input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field id="tb-to" label="To">
          <input id="tb-to" className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      {tb.isLoading ? (
        <Loading rows={3} />
      ) : tb.error ? (
        <ErrorNote error={tb.error} retry={() => void tb.refetch()} />
      ) : (tb.data?.rows ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="bookings" title="No entries in this period" />
        </div>
      ) : (
        <div className="card">
          <table className="ledger">
            <thead>
              <tr>
                <th>Account</th>
                <th className="r">Debit</th>
                <th className="r">Credit</th>
              </tr>
            </thead>
            <tbody>
              {tb.data!.rows.map((r) => (
                <tr key={r.code}>
                  <td>
                    <span className="num">{r.code}</span> {r.name}
                    <div className="hint">{TYPE_LABEL[r.type] ?? r.type}</div>
                  </td>
                  <td className="r num">{r.debit ? formatGhs(r.debit) : ''}</td>
                  <td className="r num">{r.credit ? formatGhs(r.credit) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ paddingTop: 12, fontWeight: 700 }}>Totals {tb.data!.balanced ? <Badge tone="good">Balanced</Badge> : <Badge tone="bad">Out of balance</Badge>}</td>
                <td className="r num" style={{ fontWeight: 700 }}>{formatGhs(tb.data!.totalDebit)}</td>
                <td className="r num" style={{ fontWeight: 700 }}>{formatGhs(tb.data!.totalCredit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ journal */
function JournalView({ today }: { today: string }) {
  const { can } = useAuth();
  const [source, setSource] = useState('');
  const [account, setAccount] = useState('');
  const journal = useJournal({ ...(source ? { sourceType: source } : {}), ...(account ? { accountCode: account } : {}) });
  const accounts = useLedgerAccounts();
  const [newOpen, setNewOpen] = useState(false);
  const [reversing, setReversing] = useState<string | null>(null);
  const reversed = new Set((journal.data ?? []).filter((e) => e.reversesId).map((e) => e.reversesId!));

  return (
    <div className="stack">
      <div className="grid-2">
        <Field id="jf-src" label="Source">
          <select id="jf-src" className="select" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">All</option>
            <option value="FOLIO_ENTRY">Folio (automatic)</option>
            <option value="MANUAL">Manual journals</option>
            <option value="REVERSAL">Reversals</option>
          </select>
        </Field>
        <Field id="jf-acc" label="Account">
          <select id="jf-acc" className="select" value={account} onChange={(e) => setAccount(e.target.value)}>
            <option value="">All accounts</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.code} value={a.code}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {can('ledger.journal') ? (
        <div>
          <Button variant="primary" onClick={() => setNewOpen(true)}>
            New manual journal
          </Button>
        </div>
      ) : null}

      {journal.isLoading ? (
        <Loading />
      ) : journal.error ? (
        <ErrorNote error={journal.error} retry={() => void journal.refetch()} />
      ) : (journal.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="bookings" title="No journal entries match" />
        </div>
      ) : (
        <div className="list">
          {journal.data!.map((e) => (
            <div key={e.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{e.memo}</span>
                <Badge tone={e.sourceType === 'MANUAL' ? 'warn' : e.sourceType === 'REVERSAL' ? 'bad' : ''}>{e.sourceType === 'FOLIO_ENTRY' ? 'Folio' : e.sourceType === 'MANUAL' ? 'Manual' : 'Reversal'}</Badge>
              </div>
              <div className="hint">{formatDate(e.date, true)}</div>
              <table className="ledger">
                <tbody>
                  {e.lines.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <span className="num">{l.accountCode}</span> {l.accountName}
                      </td>
                      <td className="r num">{l.debit ? formatGhs(l.debit) : ''}</td>
                      <td className="r num">{l.credit ? formatGhs(l.credit) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {e.sourceType === 'MANUAL' && can('ledger.journal') ? (
                reversed.has(e.id) ? (
                  <span className="hint">Reversed</span>
                ) : (
                  <div>
                    <Button size="small" variant="danger" onClick={() => setReversing(e.id)}>
                      Reverse
                    </Button>
                  </div>
                )
              ) : null}
            </div>
          ))}
        </div>
      )}

      <ManualJournalDialog open={newOpen} onClose={() => setNewOpen(false)} today={today} />
      <ReverseDialog journalId={reversing} onClose={() => setReversing(null)} />
    </div>
  );
}

interface Row {
  accountCode: string;
  debit: string;
  credit: string;
}

function ManualJournalDialog({ open, onClose, today }: { open: boolean; onClose: () => void; today: string }) {
  const accounts = useLedgerAccounts();
  const inv = useInvalidateLedger();
  const toast = useToast();
  const report = useReport();
  const key = useRef(newKey());
  const [date, setDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [rows, setRows] = useState<Row[]>([
    { accountCode: '', debit: '', credit: '' },
    { accountCode: '', debit: '', credit: '' },
  ]);

  const parsed = rows.map((r) => ({ ...r, d: r.debit.trim() ? parseCedis(r.debit) : 0, c: r.credit.trim() ? parseCedis(r.credit) : 0 }));
  const badAmount = parsed.some((r) => r.d === null || r.c === null);
  const totalD = parsed.reduce((s, r) => s + (r.d ?? 0), 0);
  const totalC = parsed.reduce((s, r) => s + (r.c ?? 0), 0);
  const oneSide = parsed.every((r) => (r.d ?? 0) > 0 !== (r.c ?? 0) > 0);
  const valid = !badAmount && oneSide && parsed.every((r) => r.accountCode) && totalD === totalC && totalD > 0 && memo.trim().length >= 5 && date <= today;

  const post = useMutation({
    mutationFn: () =>
      api('/ledger/journal', {
        method: 'POST',
        idempotencyKey: key.current,
        body: { date, memo: memo.trim(), lines: parsed.map((r) => ({ accountCode: r.accountCode, ...(r.d ? { debit: r.d } : { credit: r.c }) })) },
      }),
    onSuccess: () => {
      toast.ok('Journal posted');
      key.current = newKey();
      inv();
      setMemo('');
      setRows([{ accountCode: '', debit: '', credit: '' }, { accountCode: '', debit: '', credit: '' }]);
      onClose();
    },
    onError: report,
  });

  const set = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const usable = (accounts.data ?? []).filter((a) => a.active && a.code !== '1200');

  return (
    <Modal open={open} onClose={onClose} title="New manual journal">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) post.mutate();
        }}
      >
        <p className="small muted">For things that are not guest billing, such as paying a bill or moving money between accounts. The guest ledger cannot be used here.</p>
        <div className="grid-2">
          <Field id="mj-date" label="Date" error={date > today ? 'Not in the future' : undefined}>
            <input id="mj-date" className="input" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field id="mj-memo" label="Why is this needed?">
            <input id="mj-memo" className="input" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="e.g. Electricity bill paid in cash" />
          </Field>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="stack" style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 12 }}>
            <select aria-label={`Account ${i + 1}`} className="select" value={r.accountCode} onChange={(e) => set(i, { accountCode: e.target.value })}>
              <option value="">Choose account…</option>
              {usable.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
            <div className="grid-2">
              <input aria-label={`Debit ${i + 1}`} className="input" inputMode="decimal" placeholder="Debit (GH₵)" value={r.debit} onChange={(e) => set(i, { debit: e.target.value, credit: e.target.value ? '' : r.credit })} />
              <input aria-label={`Credit ${i + 1}`} className="input" inputMode="decimal" placeholder="Credit (GH₵)" value={r.credit} onChange={(e) => set(i, { credit: e.target.value, debit: e.target.value ? '' : r.debit })} />
            </div>
          </div>
        ))}
        <div className="row">
          <Button type="button" size="small" onClick={() => setRows((rs) => [...rs, { accountCode: '', debit: '', credit: '' }])}>
            Add a line
          </Button>
          <span className={`small num ${totalD === totalC && totalD > 0 ? 'settled' : 'owing'}`}>
            Debits {formatGhs(totalD)} · Credits {formatGhs(totalC)}
            {totalD !== totalC ? ' · must be equal' : ''}
          </span>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={post.isPending}>
            Post journal
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ReverseDialog({ journalId, onClose }: { journalId: string | null; onClose: () => void }) {
  const inv = useInvalidateLedger();
  const toast = useToast();
  const report = useReport();
  const key = useRef(newKey());
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api(`/ledger/journal/${journalId}/reverse`, { method: 'POST', idempotencyKey: key.current, body: { reason: reason.trim() } }),
    onSuccess: () => {
      toast.ok('Journal reversed');
      key.current = newKey();
      setReason('');
      inv();
      onClose();
    },
    onError: report,
  });
  return (
    <Modal open={!!journalId} onClose={onClose} title="Reverse this journal?">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (reason.trim().length >= 5) m.mutate();
        }}
      >
        <p className="small muted">A new entry is posted that cancels it exactly, dated today or the first open day. The original stays on record.</p>
        <Field id="rv-reason" label="Reason">
          <input id="rv-reason" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 5} loading={m.isPending}>
            Reverse
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------- chart */
function ChartView() {
  const { can } = useAuth();
  const accounts = useLedgerAccounts();
  const inv = useInvalidateLedger();
  const toast = useToast();
  const report = useReport();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('EXPENSE');

  const add = useMutation({
    mutationFn: () => api('/ledger/accounts', { method: 'POST', body: { code, name: name.trim(), type } }),
    onSuccess: () => {
      toast.ok('Account added');
      setOpen(false);
      setCode('');
      setName('');
      inv();
    },
    onError: report,
  });
  const toggle = useMutation({
    mutationFn: (a: { code: string; active: boolean }) => api(`/ledger/accounts/${a.code}`, { method: 'PATCH', body: { active: a.active } }),
    onSuccess: () => inv(),
    onError: report,
  });

  if (accounts.isLoading) return <Loading />;
  if (accounts.error) return <ErrorNote error={accounts.error} retry={() => void accounts.refetch()} />;
  const manage = can('ledger.manage');

  return (
    <div className="stack">
      {manage ? (
        <div>
          <Button variant="primary" onClick={() => setOpen(true)}>
            Add an account
          </Button>
        </div>
      ) : null}
      <div className="card">
        <table className="ledger">
          <tbody>
            {(accounts.data ?? []).map((a) => (
              <tr key={a.id} style={a.active ? undefined : { opacity: 0.55 }}>
                <td>
                  <span className="num">{a.code}</span> {a.name}
                  <div className="hint">
                    {TYPE_LABEL[a.type]}
                    {a.isSystem ? ' · built in' : ''}
                    {a.active ? '' : ' · inactive'}
                  </div>
                </td>
                <td className="r">
                  {manage && !a.isSystem ? (
                    <Button size="small" variant="ghost" onClick={() => toggle.mutate({ code: a.code, active: !a.active })}>
                      {a.active ? 'Deactivate' : 'Activate'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add an account">
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (/^\d{4,6}$/.test(code) && name.trim().length >= 2) add.mutate();
          }}
        >
          <div className="grid-2">
            <Field id="ac-code" label="Code" hint="4 to 6 digits">
              <input id="ac-code" className="input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
            </Field>
            <Field id="ac-type" label="Type">
              <select id="ac-type" className="select" value={type} onChange={(e) => setType(e.target.value)}>
                {Object.entries(TYPE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field id="ac-name" label="Name">
            <input id="ac-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!/^\d{4,6}$/.test(code) || name.trim().length < 2} loading={add.isPending}>
              Add account
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
